import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Logger } from '../logger';
import { Events } from 'src/providers/events/events';
import { DemoDataService } from 'src/shared/services/demo-data.service';

import { timeout, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { APP_VERSION } from 'src/app/version';
import * as Sentry from '@sentry/angular';

/**
 * @deprecated Use Angular's HttpClient directly with the new interceptor pattern instead.
 *
 * This service bypasses Angular's interceptor chain and uses promises instead of observables.
 * New code should use HttpClient directly - authentication, timeout, and error handling
 * are now provided by interceptors (AuthInterceptor, TimeoutInterceptor, ErrorInterceptor).
 *
 * See CLAUDE.md for the new recommended pattern.
 *
 * DO NOT migrate existing services using this - too risky.
 * Only new code and bug fixes should use the new pattern.
 */
@Injectable()
export class HttpClientCustom {

  private readonly REQUEST_TAKING_LONG_THRESHOLD = 6000;
  url = '';
  constructor(
    private http: HttpClient,
    private logger: Logger,
    private events: Events,
    private demoDataService: DemoDataService
  ) {}

  get = (url, _data = {}, timeoutMs = 20000, checkDemoData = false) => {
    if (checkDemoData && this.demoDataService.isDemoAccount()) {
      const demoData = this.demoDataService.getDemoData(url);
      if (demoData !== null) {
        return Promise.resolve(demoData);
      }
    }
    return this.request('GET', url, {}, timeoutMs);
  };

  post = (url, data, timeoutMs = 20000) => this.request('POST', url, data, timeoutMs);

  put = (url, data, timeoutMs = 20000) => this.request('PUT', url, data, timeoutMs);

  delete = (url, data: {} | undefined = undefined, timeoutMs = 20000) => this.request('DELETE', url, data, timeoutMs);

  request(method, url, data = {}, timeoutMilliseconds): Promise<any> {
    const normalizedUrl = url.startsWith('/') ? url.slice(1) : url;
    this.url = this.isExternalUrl(url) ? url : `${environment.api_url}${normalizedUrl}`;

    const takingLongTimeoutId = setTimeout(() => {
      this.onRequestTakingTooLong(method, url);
    }, this.REQUEST_TAKING_LONG_THRESHOLD);

    return this.http
      .request(method, this.url, this.payload(data, method))
      .pipe(
        timeout(timeoutMilliseconds),
        // Clear on errors too: a request that fails fast is not slow.
        finalize(() => clearTimeout(takingLongTimeoutId))
      ).toPromise()
      .catch( error => {
        this.onRequestFailed(method, url, error);
        return this.onError(error, this.url, data);
      });
  }

  getFullRequest = (url, _data = {}, timeoutMs = 20000) => this.fullRequest('GET', url, {}, timeoutMs);

  fullRequest(method, url, data = {}, timeoutMilliseconds): Promise<any> {
    const normalizedUrl = url.startsWith('/') ? url.slice(1) : url;
    this.url = this.isExternalUrl(url) ? url : `${environment.api_url}${normalizedUrl}`;
    return this.http
      .request(method, this.url, {
        ...this.payload(data, method),
        observe: 'response'
      })
      .pipe(
        timeout(timeoutMilliseconds)
      ).toPromise()
      .catch(error => this.onError(error, this.url, data));
  }

  header(): HttpHeaders {
    const tokenType = localStorage.getItem('tidy_token_type');
    const token = localStorage.getItem('tidy_token');
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    };
    if (tokenType && token) {
      headers['Authorization'] = `${tokenType} ${token}`;
    }
    if (!this.isExternalUrl(this.url)) {
      headers['X-App-Version'] = APP_VERSION;
    }
    const httpHeaders = new HttpHeaders(headers);
    return httpHeaders;
  }

  payload(data: any, _method: string): any {
    return {
      headers: this.header(),
      body: JSON.stringify(data, (k, v) => v === undefined ? null : v),
    }
  }

  checkAuthToken(error) {
    if (error instanceof HttpErrorResponse) {
      if (error?.error?.error?.message === 'The access token is invalid' || error?.error?.error?.code === 401) {
        this.events.publish('authToken:isInvalid');
      }
    }
  }

  onError(error, url, data): Promise<any> {

    this.checkAuthToken(error);

    this.logger.requestError(error, url, data);

    if (error?.error) {
      const apiError = error.error;
      if (apiError.error) {
        return Promise.reject({ code: apiError.error.code, message: apiError.error.message, err : apiError });
      }
      return Promise.reject(apiError);
    }

    return Promise.reject(error);
  }

  isExternalUrl(url): Boolean {
    return (url.indexOf('https://') === 0 || url.indexOf('http://') === 0);
  }

  private onRequestTakingTooLong(method: string, url: string): void {
    const event: Sentry.Event = {
      message: `Request ${method} ${url} took longer than expected`,
      level: 'warning',
      extra: {
        method,
        url
      }
    };

    Sentry.captureEvent(event);
  }

  // Logger is a no-op, so this is the only report of a failed request. The request body is left out (it can hold passwords).
  private onRequestFailed(method: string, url: string, error: any): void {
    const status = error?.status ?? error?.name;
    const event: Sentry.Event = {
      message: `Request ${method} ${url} failed with ${status}`,
      level: 'warning',
      extra: {
        method,
        url,
        status,
        apiCode: error?.error?.error?.code,
        apiMessage: error?.error?.error?.message
      }
    };

    Sentry.captureEvent(event);
  }
}
